import { BookOpen, Mic, BrainCircuit, Accessibility } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const FEATURES = [
  {
    icon: BookOpen,
    title: "Read Anything",
    desc: "Point your camera and listen. VoiceLens reads the world around you in natural, human voices — instantly.",
    tags: ["Mail", "Bills", "Books", "Medicine labels", "Menus", "Signs", "Websites", "PDFs"],
    tagType: "chip",
    testid: "feature-card-read-anything",
  },
  {
    icon: Mic,
    title: "Voice Activated",
    desc: "No tiny buttons. No hunting through menus. Just speak naturally and VoiceLens responds.",
    tags: ["“Read this”", "“What does this say?”", "“Explain this”", "“Read my screen”"],
    tagType: "quote",
    testid: "feature-card-voice-activated",
  },
  {
    icon: BrainCircuit,
    title: "AI Explain Mode",
    desc: "Turns complicated legal, medical, and financial documents into plain English you can actually understand.",
    tags: ["Legal → Plain English", "Medical → Clear answers", "Financial → Simple summaries"],
    tagType: "chip",
    testid: "feature-card-ai-explain",
  },
  {
    icon: Accessibility,
    title: "Accessibility First",
    desc: "Designed with — not just for — people with low vision and dyslexia. Every detail earns your independence.",
    tags: ["Large text", "High contrast", "Voice navigation", "Simple interface"],
    tagType: "chip",
    testid: "feature-card-accessibility-first",
  },
];

export default function Features() {
  return (
    <section id="why" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" aria-labelledby="why-heading">
      <span id="why-heading" className="sr-only">Why VoiceLens AI</span>
      <SectionHeading
        eyebrow={{ index: "01", label: "Why VoiceLens" }}
        title={<>Reading should never be a <span className="text-glow-gradient">barrier.</span></>}
        sub="Four capabilities, one goal: giving you instant, effortless access to every word that matters."
      />

      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {FEATURES.map(({ icon: Icon, title, desc, tags, tagType, testid }, i) => (
          <Reveal key={title} delay={i * 0.1}>
            <article
              data-testid={testid}
              className="card-glow glass-panel group h-full rounded-3xl p-8"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#00D4FF] shadow-[0_0_22px_rgba(0,212,255,0.35)] transition-transform duration-300 group-hover:scale-110">
                <Icon className="h-7 w-7 text-white" strokeWidth={2.2} aria-hidden="true" />
              </div>
              <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{title}</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-300">{desc}</p>
              <ul className="mt-6 flex flex-wrap gap-2" aria-label={`${title} examples`}>
                {tags.map((tag) => (
                  <li
                    key={tag}
                    className={
                      tagType === "quote"
                        ? "rounded-full border border-[#00D4FF]/30 bg-[#00D4FF]/8 px-4 py-1.5 text-sm font-medium italic text-[#00D4FF]"
                        : "rounded-full border border-slate-500/30 bg-slate-400/8 px-4 py-1.5 text-sm font-medium text-slate-200"
                    }
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-12 text-center">
        <p className="font-display text-xl font-semibold text-slate-200 sm:text-2xl" data-testid="features-tagline">
          Built for <span className="text-glow-gradient">independence.</span>
        </p>
      </Reveal>
    </section>
  );
}
